/**
 * SidebarDrawer.jsx
 * 
 * A permanent sidebar that can be toggled open or closed and pinned in place.
 * It contains navigation items and can display additional content via the `children` prop.
 * The drawer state can be dynamically adjusted based on user interactions, such as mouse hover and clicks.
 * 
 * @component
 * @example
 * return (
 *   <SidebarDrawer>
 *     <CustomComponent />
 *   </SidebarDrawer>
 * )
 */

import PropTypes from "prop-types";

import { forwardRef, useCallback, useState } from "react";

import DrawerClosedIcon from "@mui/icons-material/ChevronRight";
import DrawerPinnedIcon from "@mui/icons-material/ChevronLeft";
import DrawerOpenedIcon from "@mui/icons-material/PushPin";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PunchClockOutlinedIcon from '@mui/icons-material/PunchClockOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';

import Box from "@mui/material/Box";
import Divider from '@mui/material/Divider';
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MuiDrawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";

import { styled } from "@mui/material/styles";

const drawerWidth = 240;

/**
 * Defines the style for the drawer when it is closed.
 * The drawer width is minimized, and overflow is hidden.
 *
 * @param {Object} theme - The current theme object.
 * @returns {Object} The styles to apply when the drawer is closed.
 */
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(3)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(3)} + 1px)`,
  },
});

/**
 * Defines the style for the drawer when it is open.
 * The drawer width is expanded to the full width.
 *
 * @param {Object} theme - The current theme object.
 * @returns {Object} The styles to apply when the drawer is open.
 */
const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  width: drawerWidth,
});

/**
 * Styled MuiDrawer component that switches between opened and closed states.
 *
 * @param {Object} theme - The current theme object.
 * @param {boolean} open - The current open state of the drawer.
 * @returns {Object} The styles for the MuiDrawer component.
 */
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open ? openedMixin(theme) : closedMixin(theme)),
  "& .MuiDrawer-paper": open ? openedMixin(theme) : closedMixin(theme),
}));

/**
 * Styled Box component that acts as the body of the drawer.
 * It fades in and out based on whether the drawer is open.
 *
 * @param {Object} theme - The current theme object.
 * @param {boolean} open - The current open state of the drawer.
 * @returns {Object} The styles for the DrawerBody component.
 */
const DrawerBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  height: "100%",
  marginTop: theme.spacing(-3.5),
  transition: "opacity 0.3s ease",
  opacity: open ? 1 : 0,
  overflowY: "auto",
}));

/**
 * Styled Box component for the header of the drawer.
 * It contains the toggle button to open/close the drawer.
 *
 * @param {Object} theme - The current theme object.
 * @returns {Object} The styles for the DrawerHeader component.
 */
const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
}));

/**
 * DrawerStateIcon renders different icons based on whether the drawer is open or pinned.
 * - Closed: ChevronRight icon
 * - Pinned: ChevronLeft icon with hover effect for opacity
 * - Opened: PushPin icon
 *
 * @param {boolean} isOpen - Whether the drawer is open or not.
 * @param {boolean} isPinned - Whether the drawer is pinned.
 * @returns {JSX.Element} The appropriate icon based on the drawer's state.
 */
const DrawerStateIcon = ({ isOpen, isPinned }) => {
  if (!isOpen) return <DrawerClosedIcon />;
  if (isPinned) return <DrawerPinnedIcon sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }} />;
  return <DrawerOpenedIcon sx={{ fontSize: "70%", mt: 0.625, mb: 0.25 }} />;
};

/**
 * SidebarDrawer component is a permanent drawer that can be toggled and pinned.
 * It contains navigation items and can display additional children content.
 *
 * The drawer's open and pinned states are managed internally and can be controlled via mouse interactions or the toggle button.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} [props.children] - Additional content to be displayed inside the drawer.
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref to the root Drawer element.
 * @returns {JSX.Element} The rendered SidebarDrawer component.
 *
 * @example
 * return (
 *   <SidebarDrawer>
 *     <CustomComponent />
 *   </SidebarDrawer>
 * )
 */
const SidebarDrawer = forwardRef(({
  children,
}, ref) => {
  /**
   * isDrawerOpen and isDrawerPinned manage the open/close state of the drawer.
   * - isDrawerOpen controls whether the drawer is visible or collapsed.
   * - isDrawerPinned controls whether the drawer is pinned in place, preventing it from collapsing.
   */
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isDrawerPinned, setIsDrawerPinned] = useState(true);

  const updateDrawerState = (pinned) => {
    setIsDrawerOpen(pinned);
    setIsDrawerPinned(pinned);
  };

  const collapseDrawerOnMouseLeave = () => {
    if (!isDrawerPinned) {
      updateDrawerState(false);
    }
  };

  const expandDrawerOnHover = useCallback((event) => {
    const isMouseOnDrawerHeader = event.target.closest(".MuiDrawerHeader");
    if (!isMouseOnDrawerHeader) {
      setIsDrawerOpen(true);
    }
  }, []);

  const toggleDrawer = () => {
    updateDrawerState(!isDrawerPinned);
  };

  const iconButtonStyles = {
    zIndex: 2,
    ...(!isDrawerOpen && { pr: 0 }),
  };

  const paperStyles = {
    display: "flex",
    flexDirection: "column",
    top: "104px",
    height: 'calc(100vh - 112px)',
    overflow: 'hidden',
    zIndex: 2,
  };

  return (
    <Drawer
      ref={ref}
      variant="permanent"
      open={isDrawerOpen}
      onMouseLeave={collapseDrawerOnMouseLeave}
      PaperProps={{
        elevation: 1,
        square: false,
        sx: paperStyles,
      }}>
      <DrawerHeader open={isDrawerOpen} className="MuiDrawerHeader">
        <IconButton
          onClick={toggleDrawer}
          sx={iconButtonStyles}
        >
          <DrawerStateIcon
            isOpen={isDrawerOpen}
            isPinned={isDrawerPinned}
          />
        </IconButton>
      </DrawerHeader>
      <DrawerBody open={isDrawerOpen} onMouseEnter={expandDrawerOnHover}>
        <List>
          {[
            { text: "Startsida", icon: <HomeOutlinedIcon /> },
            { text: "Mina uppgifter", icon: <TaskOutlinedIcon /> },
            { text: "Tidredovisning", icon: <PunchClockOutlinedIcon /> },
            { text: "Inkorg", icon: <NotificationsNoneOutlinedIcon /> }
          ].map(({ text, icon: Icon }, index) => (
            <ListItem key={text} disablePadding sx={{ display: "block", p: 0 }}>
              <ListItemButton sx={{ minHeight: 34, px: 2.5 }}>
                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                  {Icon}
                </ListItemIcon>
                <ListItemText primary={text} sx={{ my: 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mt: 1, mb: 2.75 }} />
        {isDrawerOpen && (
          <Stack direction="column" spacing={1} sx={{ padding: 1 }}>
            {children}
          </Stack>
        )}
      </DrawerBody>

    </Drawer>
  );
});

SidebarDrawer.propTypes = {
  children: PropTypes.node,
};

SidebarDrawer.displayName = "SidebarDrawer";

export default SidebarDrawer;
